import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { focusElement } from '../../utils/useFocusOnOpen';
import { resolveLinkedRecordNav } from '../../utils/linkedRecordNav';
import { hasErrors, useFieldErrors } from '../../utils/formValidation';
import PhotoSection from '../PhotoSection';
import PhenotypeEditor from '../PhenotypeEditor';
import MapGpsPicker from '../MapGpsPicker';
import DangerZone from '../DangerZone';
import QRCodeLabel from '../QRCodeLabel';
import {
  CONFIDENCE, EMPTY_PLATE_SUB, EMPTY_SLANT_SUB, GROWTH_PATTERNS, PlateFields, RANKS, SIZES, STAGES, SUBSTRATES,
  SlantFields, defaultSlantSub, plateSubErrors, slantSubErrors,
} from './IntakeFields';
import {
  btnStyle, errStyle, fullRow, gridStyle, inputStyle, labelStyle, subFormStyle,
} from './styles';

/*
 * One intake record (external_collections), opened from Home: what the old
 * External page's record view did, in the hub's wide side drawer. The hub
 * opens it from its URL (?intake=<external_id>), so a code lookup, the scan
 * station, an isolate's gallery link or its Taxonomy tab can link straight
 * to it, and closing it takes the parameter away.
 *
 * It shows the record and its QR label, edits every field of it (collection
 * managers and internal experts, PUT /external/{id}), registers more lab
 * material on it -- a plate (POST /plates) or tubes (POST
 * /slants/external-intake) -- lists the plates and tubes already made from it
 * (GET /external/{id}/family), and holds its photos and phenotype. Deleting
 * it is offered at the foot, away from the close button (DangerZone), and
 * only when GET /external/{id} says this caller may (`actions.delete`,
 * routes/ownership.py: a system admin, a collection manager, or whoever
 * received it while the lab has done nothing with it).
 *
 * Props:
 *   externalId  the intake to show
 *   user        the signed-in user, for what they may change
 *   onClose     closes the drawer
 *   onNavigate(path, state)
 *               the hub's navigation, for a plate or tube chip
 *   onChanged() something was written: the hub reads its rows and counts again
 */

const LAB_STATUSES = ['not_submitted', 'in_progress', 'completed', 'rejected', 'no_lab_sample', 'pending_intake'];

const EMPTY_EDIT = {
  donor_name: '', donor_contact: '', original_collector: '', original_collection_date: '',
  original_location_name: '', original_gps_lat: '', original_gps_lng: '',
  given_name: '',
  substrate: '', fruiting_body_size: '', phenological_stage: '',
  fruiting_body_count: '', growth_pattern: '',
  taxon_name: '', taxonomic_rank: '', confidence: '',
  lab_status: '', general_notes: '',
  fruiting_body_size_estimated: false,
};

const hasRole = (user, roles) => !!user?.roles?.some((r) => roles.includes(r));

/** The record as the edit form holds it: every field a string (or the one boolean). */
function editFormFor(record) {
  const form = { ...EMPTY_EDIT };
  Object.keys(EMPTY_EDIT).forEach((k) => {
    if (record?.[k] !== null && record?.[k] !== undefined) form[k] = record[k];
  });
  if (typeof form.original_collection_date === 'string') {
    form.original_collection_date = form.original_collection_date.slice(0, 10);
  }
  return form;
}

/** The PUT body: an empty choice is null, never '' (the columns are ENUMs). */
function editBody(f) {
  return {
    ...f,
    original_gps_lat:         f.original_gps_lat         || null,
    original_gps_lng:         f.original_gps_lng         || null,
    original_collection_date: f.original_collection_date || null,
    fruiting_body_size:       f.fruiting_body_size       || null,
    phenological_stage:       f.phenological_stage       || null,
    fruiting_body_count:      f.fruiting_body_count      || null,
    growth_pattern:           f.growth_pattern           || null,
    substrate:                f.substrate                || null,
    taxonomic_rank:           f.taxonomic_rank           || null,
    confidence:               f.confidence               || null,
    lab_status:               f.lab_status               || null,
  };
}

export default function IntakeRecordDrawer({ externalId, user, onClose, onNavigate, onChanged }) {
  const { t } = useTranslation(['external', 'collections', 'common', 'isolates']);
  const canEditRecord = hasRole(user, ['collection_manager', 'internal_expert']);
  const canCreateLabMaterial = hasRole(user, ['collection_manager', 'lab_technician', 'lab_manager']);

  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [family, setFamily] = useState({ plates: [], slants: [] });
  const [protocols, setProtocols] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [plateOpen, setPlateOpen] = useState(false);
  const [slantOpen, setSlantOpen] = useState(false);
  const [plateSub, setPlateSub] = useState(EMPTY_PLATE_SUB);
  const [slantSub, setSlantSub] = useState(EMPTY_SLANT_SUB);
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const plateErrs = useFieldErrors();
  const slantErrs = useFieldErrors();

  const headingRef = useRef(null);

  const loadFamily = useCallback(() => {
    api.get(`/external/${externalId}/family`)
      .then((res) => setFamily({ plates: res?.data?.plates || [], slants: res?.data?.slants || [] }))
      .catch(() => setFamily({ plates: [], slants: [] }));
  }, [externalId]);

  // The record, what was made from it, and the media list the plate and the
  // tubes choose from -- read again for every intake opened.
  useEffect(() => {
    let live = true;
    setRecord(null);
    setLoadError('');
    setEditing(false);
    setPlateOpen(false);
    setSlantOpen(false);
    setActionError('');
    api.get(`/external/${externalId}`)
      .then((res) => {
        if (!live) return;
        const data = res?.data || null;
        setRecord(data);
        setEditForm(editFormFor(data));
        setSlantSub(defaultSlantSub(data?.qr_uid));
      })
      .catch((e) => {
        if (!live) return;
        setLoadError(e?.response?.status === 404 ? t('detailModal.notFound')
          : (e?.response?.data?.error || t('failedToLoad')));
      });
    loadFamily();
    api.get('/protocols')
      .then((res) => { if (live) setProtocols(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [externalId, loadFamily, t]);

  // The heading takes focus as the drawer opens; Escape closes it, unless
  // the delete confirmation is the thing being answered.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  const deleteOpenRef = useRef(false);   // DangerZone's onOpenChange keeps it
  useEffect(() => {
    focusElement(headingRef.current);
    const onKey = (e) => { if (e.key === 'Escape' && !deleteOpenRef.current) closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [externalId]);

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const submitEdit = (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    api.put(`/external/${externalId}`, editBody(editForm))
      .then(() => {
        setEditing(false);
        api.get(`/external/${externalId}`)
          .then((res) => { setRecord(res.data); setEditForm(editFormFor(res.data)); })
          .catch(() => {});
        onChanged?.();
      })
      .catch((err) => setEditError(err.response?.data?.error || t('editModal.updateFailed')))
      .finally(() => setEditSaving(false));
  };

  const registerProtocol = (sub) => {
    const value = sub.protocol === 'other' ? sub.protocolOther.trim() : sub.protocol;
    // Best effort: a medium typed as "Other" is offered next time.
    if (sub.protocol === 'other' && value) api.post('/protocols', { name: value }).catch(() => {});
    return value;
  };

  const submitPlate = () => {
    const errors = plateSubErrors(plateSub, 'detailModal.plateProtocolRequiredError');
    if (hasErrors(errors)) { setActionError(''); plateErrs.show(errors); return; }
    plateErrs.reset();
    setActionSaving(true);
    setActionError('');
    const protocol = registerProtocol(plateSub);
    api.post('/plates', {
      origin: 'external_collection',
      external_id: externalId,
      protocol,
      temperature_c: parseFloat(plateSub.temperature) || 25,
      create_control_pair: plateSub.createControlPair,
      notes: plateSub.notes.trim() || undefined,
    })
      .then(() => { setPlateOpen(false); setPlateSub(EMPTY_PLATE_SUB); loadFamily(); onChanged?.(); })
      .catch((err) => setActionError(err.response?.data?.error || t('detailModal.createPlateFailed')))
      .finally(() => setActionSaving(false));
  };

  const submitSlants = () => {
    const entries = slantSub.entries.filter((en) => en.code.trim());
    const errors = slantSubErrors(slantSub, 'detailModal.slantCodeRequiredError');
    if (hasErrors(errors)) { setActionError(''); slantErrs.show(errors); return; }
    slantErrs.reset();
    setActionSaving(true);
    setActionError('');
    const protocol = registerProtocol(slantSub);
    api.post('/slants/external-intake', {
      external_id: externalId,
      slants: entries.map((en) => ({ code: en.code.trim(), role: en.role })),
      protocol: protocol || undefined,
    })
      .then(() => {
        setSlantOpen(false);
        setSlantSub(defaultSlantSub(record?.qr_uid));
        loadFamily();
        onChanged?.();
      })
      .catch((err) => setActionError(err.response?.data?.error || t('detailModal.createSlantsFailed')))
      .finally(() => setActionSaving(false));
  };

  const goTo = (table, id) => {
    const nav = resolveLinkedRecordNav(table, id);
    if (nav) onNavigate?.(nav.path, nav.state);
  };

  const confirmDelete = () => api.delete(`/external/${externalId}`)
    .then(() => { deleteOpenRef.current = false; onChanged?.(); onClose?.(); });

  const facts = record ? [
    [t('form.donorContactLabel'), record.donor_contact],
    [t('form.originalCollectorLabel'), record.original_collector],
    [t('form.originalCollectionDateLabel'), record.original_collection_date
      ? new Date(record.original_collection_date).toLocaleDateString() : null],
    [t('form.locationNameLabel'), record.original_location_name],
    [t('form.givenNameLabel'), record.given_name],
    [t('form.substrateLabel'), record.substrate ? t(`collections:substrate.${record.substrate}`) : null],
    [t('form.sizeLabel'), record.fruiting_body_size ? t(`collections:size.${record.fruiting_body_size}`) : null],
    [t('form.stageLabel'), record.phenological_stage ? t(`collections:stage.${record.phenological_stage}`) : null],
    [t('form.countLabel'), record.fruiting_body_count],
    [t('form.growthPatternLabel'), record.growth_pattern ? t(`collections:growthPattern.${record.growth_pattern}`) : null],
    [t('editModal.taxonNameLabel'), record.taxon_name, 'ltr'],
    [t('form.rankLabel'), record.taxonomic_rank ? t(`common:rank.${record.taxonomic_rank}`) : null],
    [t('form.confidenceLabel'), record.confidence ? t(`common:confidence.${record.confidence}`) : null],
    [t('editModal.labStatusLabel'), record.lab_status ? t(`collections:labStatus.${record.lab_status}`) : null],
  ] : [];

  return (
    <div className="hub-drawer-backdrop" data-testid="intake-record-backdrop">
      <aside className="hub-drawer hub-drawer--wide" role="dialog" aria-modal="true"
        aria-labelledby="intake-record-title" data-testid="intake-record-drawer">
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h3 id="intake-record-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
              {record?.qr_uid ? <bdi dir="ltr">{record.qr_uid}</bdi> : t('detailModal.recordInfoTitle')}
            </h3>
            {record && (
              <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--c-text-muted-fg)' }}>
                {record.donor_name || record.original_collector || '—'}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} title={t('isolates:tip.closeDrawer')}
            aria-label={t('isolates:tip.closeDrawer')} data-testid="intake-record-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
              color: 'var(--c-text-muted-fg)', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {loadError && <p role="alert" style={{ ...errStyle, fontSize: 14 }}>{loadError}</p>}
        {!record && !loadError && <p style={dimStyle}>{t('common:loading')}</p>}

        {record && (
          <>
            {(record.specimen_code || record.lab_code) && (
              <p style={{ margin: '0 0 10px', fontSize: 13 }} data-testid="intake-record-isolate">
                {record.specimen_code && <bdi dir="ltr" style={codeStyle}>{record.specimen_code}</bdi>}
                {record.lab_code && (
                  <bdi dir="ltr" style={{ ...codeStyle, marginInlineStart: 10 }}>{record.lab_code}</bdi>
                )}
              </p>
            )}
            {record.qr_uid && <div style={{ marginBottom: 12 }}><QRCodeLabel value={record.qr_uid} /></div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <strong style={{ fontSize: 14 }}>{t('detailModal.recordInfoTitle')}</strong>
              {canEditRecord && !editing && (
                <button type="button" onClick={() => setEditing(true)} title={t('tip.editRecord')}
                  data-testid="intake-record-edit" style={{ ...btnStyle('var(--c-primary)'), padding: '4px 10px', fontSize: 12 }}>
                  {t('actions.edit')}
                </button>
              )}
            </div>

            {editing ? (
              <form onSubmit={submitEdit} data-testid="intake-record-form">
                {editError && <p role="alert" style={errStyle}>{editError}</p>}
                <div style={gridStyle}>
                  <label style={labelStyle}>
                    {t('editModal.donorNameLabel')}
                    <input name="donor_name" value={editForm.donor_name} onChange={handleEditChange} style={inputStyle} title={t('tip.donorName')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.donorContactLabel')}
                    <input name="donor_contact" value={editForm.donor_contact} onChange={handleEditChange} style={inputStyle} title={t('tip.donorContact')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.originalCollectorLabel')}
                    <input name="original_collector" value={editForm.original_collector} onChange={handleEditChange} style={inputStyle} title={t('tip.originalCollector')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.originalCollectionDateLabel')}
                    <input name="original_collection_date" type="date" value={editForm.original_collection_date} onChange={handleEditChange} style={inputStyle} title={t('tip.originalCollectionDate')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.locationNameLabel')}
                    <input name="original_location_name" value={editForm.original_location_name} onChange={handleEditChange} style={inputStyle} title={t('tip.locationName')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.givenNameLabel')}
                    <input name="given_name" value={editForm.given_name} onChange={handleEditChange} maxLength={120} dir="auto"
                      style={inputStyle} title={t('tip.givenName')} data-testid="intake-edit-given-name" />
                  </label>
                  <label style={labelStyle}>
                    {t('form.substrateLabel')}
                    <select name="substrate" value={editForm.substrate} onChange={handleEditChange} style={inputStyle} title={t('tip.substrate')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {SUBSTRATES.map((s) => <option key={s} value={s}>{t(`collections:substrate.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t('form.sizeLabel')}
                    <select name="fruiting_body_size" value={editForm.fruiting_body_size} onChange={handleEditChange} style={inputStyle} title={t('tip.size')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {SIZES.map((s) => <option key={s} value={s}>{t(`collections:size.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
                    <input type="checkbox" name="fruiting_body_size_estimated" checked={!!editForm.fruiting_body_size_estimated} onChange={handleEditChange} title={t('tip.sizeEstimated')} />
                    {t('form.sizeEstimateLabel')}
                  </label>
                  <label style={labelStyle}>
                    {t('form.stageLabel')}
                    <select name="phenological_stage" value={editForm.phenological_stage} onChange={handleEditChange} style={inputStyle} title={t('tip.stage')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {STAGES.map((s) => <option key={s} value={s}>{t(`collections:stage.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t('form.countLabel')}
                    <input name="fruiting_body_count" type="number" min="1" step="1" value={editForm.fruiting_body_count} onChange={handleEditChange} style={inputStyle} title={t('tip.count')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.growthPatternLabel')}
                    <select name="growth_pattern" value={editForm.growth_pattern} onChange={handleEditChange} style={inputStyle} title={t('tip.growthPattern')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {GROWTH_PATTERNS.map((g) => <option key={g} value={g}>{t(`collections:growthPattern.${g}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t('editModal.taxonNameLabel')}
                    <input name="taxon_name" value={editForm.taxon_name} onChange={handleEditChange} style={inputStyle} dir="ltr" title={t('tip.taxonName')} />
                  </label>
                  <label style={labelStyle}>
                    {t('form.rankLabel')}
                    <select name="taxonomic_rank" value={editForm.taxonomic_rank} onChange={handleEditChange} style={inputStyle} title={t('tip.rank')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {RANKS.map((r) => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t('form.confidenceLabel')}
                    <select name="confidence" value={editForm.confidence} onChange={handleEditChange} style={inputStyle} title={t('tip.confidence')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {CONFIDENCE.map((c) => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
                    </select>
                  </label>
                  <label style={labelStyle}>
                    {t('editModal.labStatusLabel')}
                    <select name="lab_status" value={editForm.lab_status} onChange={handleEditChange} style={inputStyle} title={t('tip.labStatus')}>
                      <option value="">{t('common:unchangedPlaceholder')}</option>
                      {LAB_STATUSES.map((s) => <option key={s} value={s}>{t(`collections:labStatus.${s}`)}</option>)}
                    </select>
                  </label>
                  <label style={{ ...labelStyle, ...fullRow }}>
                    {t('editModal.generalNotesLabel')}
                    <textarea name="general_notes" value={editForm.general_notes} onChange={handleEditChange} rows={3} style={inputStyle} title={t('tip.generalNotes')} />
                  </label>
                </div>
                {/* A donor's original collection point, never this device's own
                    place: no one-tap "use my location" here. */}
                <MapGpsPicker
                  lat={editForm.original_gps_lat}
                  lng={editForm.original_gps_lng}
                  hintText={t('form.gpsMapHint')}
                  onChange={(lat, lng) => setEditForm((f) => ({ ...f, original_gps_lat: lat, original_gps_lng: lng }))}
                  showRecapture={false}
                />
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button type="submit" disabled={editSaving} style={btnStyle('var(--c-primary)')} title={t('tip.saveEdit')}
                    data-testid="intake-record-save">
                    {editSaving ? t('common:saving') : t('common:save')}
                  </button>
                  <button type="button" onClick={() => { setEditing(false); setEditForm(editFormFor(record)); setEditError(''); }}
                    style={btnStyle('var(--c-text-muted)')} title={t('tip.cancelEdit')}>
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '6px 24px', fontSize: 14, marginBottom: 4 }}
                data-testid="intake-record-facts">
                {facts.map(([label, val, dir]) => ((val !== null && val !== undefined && val !== '') ? (
                  <div key={label}><span style={{ color: 'var(--c-text-muted-fg)' }}>{label}: </span><span dir={dir}>{val}</span></div>
                ) : null))}
                {record.general_notes && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('editModal.generalNotesLabel')}: </span>{record.general_notes}
                  </div>
                )}
              </div>
            )}

            <PhotoSection
              fetchUrl={`/external/${externalId}/photos`}
              uploadBody={{ external_id: externalId }}
              taxonName={record.taxon_name}
              sourceType="external_collection"
              annotatable
            />

            <PhenotypeEditor anchor="external" recordId={externalId} canEdit={canEditRecord} />

            <div style={sectionStyle}>
              <div style={sectionHead}>
                <strong style={{ fontSize: 14 }}>{t('detailModal.linkedPlatesTitle')}</strong>
                {canCreateLabMaterial && (
                  <button type="button" onClick={() => { setPlateOpen((s) => !s); setActionError(''); plateErrs.reset(); }}
                    title={t('tip.createAnotherPlate')} data-testid="intake-record-add-plate"
                    style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }}>
                    {plateOpen ? t('common:cancel') : t('detailModal.createAnotherPlateBtn')}
                  </button>
                )}
              </div>
              {family.plates.length === 0 ? (
                <p style={dimStyle}>{t('detailModal.noLinkedPlates')}</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: plateOpen ? 12 : 0 }}>
                  {family.plates.map((p) => (
                    <button key={p.id} type="button" onClick={() => goTo('plate_culture', p.id)} title={t('tip.goToPlate')}
                      style={chipStyle}>
                      <span dir="ltr">{p.code}</span>
                      {p.protocol && <span dir="ltr" style={{ color: 'var(--c-text-muted-fg)' }}>({p.protocol})</span>}
                    </button>
                  ))}
                </div>
              )}
              {canCreateLabMaterial && plateOpen && (
                <div style={subFormStyle} ref={plateErrs.containerRef}>
                  {actionError && <p role="alert" style={errStyle}>{actionError}</p>}
                  <PlateFields value={plateSub} protocols={protocols} t={t} suggestTaxonName={record.taxon_name}
                    idPrefix="detail" errors={plateErrs.errors}
                    onChange={(v) => { setPlateSub(v); plateErrs.clear((f) => f === 'protocol' || f === 'protocolOther'); }} />
                  <button type="button" onClick={submitPlate} disabled={actionSaving} title={t('tip.confirmCreatePlate')}
                    style={btnStyle('var(--c-primary)')} data-testid="intake-record-plate-submit">
                    {actionSaving ? t('common:saving') : t('detailModal.confirmCreatePlateBtn')}
                  </button>
                </div>
              )}
            </div>

            <div style={sectionStyle}>
              <div style={sectionHead}>
                <strong style={{ fontSize: 14 }}>{t('detailModal.linkedSlantsTitle')}</strong>
                {canCreateLabMaterial && (
                  <button type="button" onClick={() => { setSlantOpen((s) => !s); setActionError(''); slantErrs.reset(); }}
                    title={t('tip.registerMoreSlants')} data-testid="intake-record-add-slants"
                    style={{ ...btnStyle('var(--c-primary)'), padding: '5px 12px', fontSize: 13 }}>
                    {slantOpen ? t('common:cancel') : t('detailModal.registerMoreSlantsBtn')}
                  </button>
                )}
              </div>
              {family.slants.length === 0 ? (
                <p style={dimStyle}>{t('detailModal.noLinkedSlants')}</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: slantOpen ? 12 : 0 }}>
                  {family.slants.map((s) => (
                    <button key={s.id} type="button" onClick={() => goTo('mushroom_bank', s.id)} title={t('tip.goToSlant')}
                      style={chipStyle}>
                      <span dir="ltr">{s.code}</span>
                    </button>
                  ))}
                </div>
              )}
              {canCreateLabMaterial && slantOpen && (
                <div style={subFormStyle} ref={slantErrs.containerRef}>
                  {actionError && <p role="alert" style={errStyle}>{actionError}</p>}
                  <SlantFields value={slantSub} protocols={protocols} t={t} qrUid={record.qr_uid} suggestTaxonName={record.taxon_name}
                    idPrefix="detail" errors={slantErrs.errors}
                    onChange={(v) => { setSlantSub(v); slantErrs.clear('slantCode'); }} />
                  <button type="button" onClick={submitSlants} disabled={actionSaving} title={t('tip.confirmRegisterSlants')}
                    style={btnStyle('var(--c-primary)')} data-testid="intake-record-slants-submit">
                    {actionSaving ? t('common:saving') : t('detailModal.confirmRegisterSlantsBtn')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {record?.actions?.delete && (
          <DangerZone testId="intake-record-delete" label={t('common:delete')} title={t('tip.deleteRecord')}
            confirmTitle={t('actions.deleteConfirmTitle')}
            consequences={[t('actions.deleteConsequence1'), t('actions.deleteConsequence2')]}
            failedText={t('actions.deleteFailed')} onConfirm={confirmDelete}
            onOpenChange={(open) => { deleteOpenRef.current = open; }} />
        )}
      </aside>
    </div>
  );
}

const dimStyle = { fontSize: 13, color: 'var(--c-text-faint-fg)' };
const codeStyle = { fontFamily: 'monospace', fontSize: 12 };
const sectionStyle = { marginTop: 20, borderTop: '1px solid var(--c-border)', paddingTop: 16 };
const sectionHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 };
const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--c-border-strong)',
  borderRadius: 14, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--c-primary-fg)',
};
